// This component is workable to get Rapid7 and Tenable Information.

import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { GetEntityQuery, EntityTypeEnum } from 'app/API.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EntityService } from 'app/shared/entity.service';
import { ToastrService } from 'ngx-toastr';
import { Services, ServiceName } from '../../constants';
import { Azure, Integration, AWS_TITLE, QUALYS_PRODUCTS } from 'app/shared/enums/integration.enum';
import { UtilsService } from '../utils.service';
import { CURR_VIEW } from 'app/shared/enums/shared.enum';
import { CollectionService } from 'app/collection/collection.service';
import { CUSTOMAPIService } from 'app/custom-api.service';
import { RootEntityContextService } from 'app/shared/root-entity-context.service';
import { IntegrationMappingService } from 'app/shared/integration-mapping.service';
import { FileService } from '../file.service';
import { DeleteModalComponent } from '../delete-modal/delete-modal.component';
import saveAs from 'file-saver';

interface IntegrationModal {
  name: string;
  credentials?: string;
  isEnable?: boolean;
  isAuth?: boolean;
  inheritFromParent?: boolean;
}

interface IntegrationCreds {
  url?: string;
  username?: string;
  password?: string;
  accessKey?: string;
  secretKey?: string;
  currView?: View;
  tenantId?: string;
  clientSecret?: string;
  subscriptionId?: string;
  clientId?: string;
  roleArn?: string;
  region?: string;
  product?: string;
  apiToken?: string;
  orgId?: string;
  resourceGroupName?: string;
  workspaceName?: string;
  apiEndpointUrl?: string;
  bearerToken?: string;
  apiHostname?: string;
  integrationKey?: string;
  token?: string;
  organization?: string;
  apiId?: string;
  apiKey?: string;
  serviceAccountKey?: string;
  organizationId?: string;
}

type View = 'accessKey' | 'username' | 'token' | null;

const currViewMap = {
  tenable: CURR_VIEW.ACCESSKEY,
  nessus: CURR_VIEW.ACCESSKEY,
  rapid7: CURR_VIEW.USERNAME,
  splunk: CURR_VIEW.USERNAME,
};

@Component({
  selector: 'cygov-scan-integration-service-modal',
  templateUrl: './scan-integration-service-modal.component.html',
  styleUrls: ['./scan-integration-service-modal.component.scss'],
  standalone: false,
})
export class ScanIntegrationServiceModalComponent implements OnInit {
  @Input() entityId: string;
  @Input() isTenable: boolean;
  @Input() serviceName: string;
  @Input() subEntityId: string;
  @Input() integrationExists = false;
  @Input() hasCredentials = false;
  @Output() accountEntity: EventEmitter<any> = new EventEmitter();
  entity: GetEntityQuery;
  formData: FormGroup;
  serviceType: string;
  formDisabled = false;
  removeInProgress = false;
  integrations: IntegrationModal[] = [];
  r7Placeholder: string = 'https://rapid7.com/api/3';
  tenablePlaceholder: string = 'https://sc.tenalab.online/rest';
  nessusPlaceholder: string = 'https://nessus-server:8834';
  crowdstrikePlaceholder: string = 'api.us-2.crowdstrike.com';
  placeholder: string = this.tenablePlaceholder; // by default

  // tenable var
  currView: View = CURR_VIEW.ACCESSKEY;
  Integration = Integration; // enum;
  title: string;
  qualysPlaceholder: string = 'https://gateway.qg1.apps.qualys.com';
  qualysVMDRPlaceholder: string = 'https://qualysapi.qg1.apps.qualys.com';
  prismacloudPlaceholder: string = 'https://api.prismacloud.io';
  qualysProducts = [
    { value: 'VMDR', label: 'VMDR (Vulnerability Management, Detection and Response)' },
    { value: 'ETM', label: 'ETM (Enterprise Threat Management)' },
  ];
  // Sub-entity scoping (UI only for now — backend wiring pending)
  subEntities: Array<{ id: string; name: string; selected: boolean }> = [];
  applyToAll = true;
  showSubEntityPicker = false;
  subEntitiesLoading = false;
  isSubEntityLevel = false;
  existingIsAuth: boolean = null;
  credentialsRevealed = false;
  credentialsLoading = false;
  private _querySubEntityId: string | null = null;
  private _queryAppliedToAll = false;
  private savedViewState: View = null;

  constructor(
    public activeModal: NgbActiveModal,
    private entityService: EntityService,
    private toastrService: ToastrService,
    private collectionService: CollectionService,
    private customApi: CUSTOMAPIService,
    private rootEntityContextService: RootEntityContextService,
    private fileService: FileService,
    private integrationMappingService: IntegrationMappingService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.initAsync();
  }

  private async initAsync(): Promise<void> {
    this.entity = await this.getEntity();
    this.isSubEntityLevel = !!this.subEntityId;
    this.serviceType = Services[this.serviceName.toUpperCase()];
    await this.loadSubEntities();

    this.title = this.resolveTitle();
    this.setPlaceholder(this.serviceName);
    this.currView = currViewMap[this.serviceName] ? currViewMap[this.serviceName] : CURR_VIEW.USERNAME;

    const rootEntityId = this.entity.rootEntityId || this.entity.parentId || this.entityId;
    const { querySubEntityId, queryAppliedToAll } = await this.resolveIntegrationScope(rootEntityId);

    this._querySubEntityId = querySubEntityId;
    this._queryAppliedToAll = queryAppliedToAll;

    let credentials: IntegrationCreds;
    if (this.hasCredentials) {
      const mask = '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022';
      credentials = new Proxy({} as any, { get: () => mask });
    } else {
      credentials = await this._fetchCredentials(rootEntityId, querySubEntityId, queryAppliedToAll);
    }

    this.formData = this.buildFormGroup(credentials);

    if (this.hasCredentials && !this.credentialsRevealed) {
      this.formData.disable();
    }
  }

  private resolveTitle(): string {
    const titleMap: Record<string, string> = {
      [Integration.AZURE]: Azure.AZURE,
      [Integration.MICROSOFT]: Azure.MICROSOFT,
      [Integration.AWSSECURITYHUB]: AWS_TITLE.AWS,
      [Integration.INTUNE]: 'Microsoft Intune',
      [Integration.QUALYSETM]: QUALYS_PRODUCTS.ETM,
      [Integration.QUALYSVMDR]: QUALYS_PRODUCTS.VMDR,
      [Integration.SNYK]: 'Snyk',
      [Integration.SENTINEL]: 'Microsoft Sentinel',
      [Integration.SENTINELONE]: 'SentinelOne',
      [Integration.WIZ]: 'Wiz',
      [Integration.OKTA]: 'Okta',
      [Integration.ORCA]: 'Orca Security',
      [Integration.PRISMACLOUD]: 'Prisma Cloud',
      [Integration.DUO]: 'Duo',
      [Integration.GITHUBSECURITY]: 'GitHub Advanced Security',
      [Integration.GITLAB]: 'GitLab',
      [Integration.VERACODE]: 'Veracode',
      [Integration.SONARQUBE]: 'SonarQube',
      [Integration.KNOWBE4]: 'KnowBe4',
      [Integration.GCPSCC]: 'Google Cloud SCC',
      [Integration.SERVICENOWCMDB]: 'ServiceNow CMDB',
      [Integration.CYBERARK]: 'CyberArk',
    };
    return titleMap[this.serviceName] || this.serviceName;
  }

  private matchesServiceType(integrationType: string): boolean {
    if (!integrationType) {
      return false;
    }
    const normalize = (s: string) => s.toLowerCase().replace(/information$/, '');
    return normalize(integrationType) === normalize(this.serviceType);
  }

  private async resolveIntegrationScope(
    rootEntityId: string
  ): Promise<{ querySubEntityId: string | null; queryAppliedToAll: boolean }> {
    let querySubEntityId: string | null = this.subEntityId || null;
    let queryAppliedToAll = !this.subEntityId;

    if (!this.isSubEntityLevel) {
      try {
        const allMappings = await this.integrationMappingService.getMappingsForRoot(rootEntityId);
        const serviceRecords = allMappings.filter(
          r => this.matchesServiceType(r.integrationType) && r.isEnable !== false
        );

        if (serviceRecords.length) {
          if (serviceRecords[0].currView) {
            this.currView = serviceRecords[0].currView as View;
            this.savedViewState = serviceRecords[0].currView as View;
          }
          const hasAppliedToAll = serviceRecords.some(r => r.appliedToAll === true);
          if (hasAppliedToAll) {
            this.applyToAll = true;
            this.subEntities.forEach(se => (se.selected = true));
            queryAppliedToAll = true;
            querySubEntityId = null;
          } else {
            this.applyToAll = false;
            const sharedRecords = serviceRecords.filter(
              r =>
                r.subEntityId && r.subEntityId !== rootEntityId && !IntegrationMappingService.isSubEntityScopedRecord(r)
            );
            const selectedSet = new Set(sharedRecords.map(r => r.subEntityId));
            this.subEntities.forEach(se => (se.selected = selectedSet.has(se.id)));
            const representativeRecord = sharedRecords[0];
            if (representativeRecord) {
              querySubEntityId = representativeRecord.subEntityId;
              queryAppliedToAll = false;
            }
          }
        }
      } catch (e) {
        console.log('Failed to resolve scope from cached mappings', e);
      }
    }

    if (this.isSubEntityLevel) {
      try {
        const allMappings = await this.integrationMappingService.getMappingsForRoot(rootEntityId);
        const serviceRecords = allMappings.filter(
          r => this.matchesServiceType(r.integrationType) && r.isEnable !== false
        );
        this.applyToAll = serviceRecords.some(r => r.appliedToAll === true);
        if (this.applyToAll) {
          queryAppliedToAll = true;
          querySubEntityId = null;
        }
        if (serviceRecords.length && serviceRecords[0].currView) {
          this.currView = serviceRecords[0].currView as View;
          this.savedViewState = serviceRecords[0].currView as View;
        }
      } catch (e) {
        console.log('Failed to resolve appliedToAll scope at sub-entity level', e);
        this.applyToAll = false;
      }
    }

    return { querySubEntityId, queryAppliedToAll };
  }

  private buildFormGroup(credentials: IntegrationCreds): FormGroup {
    switch (this.serviceName) {
      case Integration.AZURE:
      case Integration.MICROSOFT:
        return new FormGroup({
          subscriptionId: new FormControl(credentials.subscriptionId),
          tenantId: new FormControl(credentials.tenantId),
          clientId: new FormControl(credentials.clientId),
          clientSecret: new FormControl(credentials.clientSecret),
        });
      case Integration.AWSSECURITYHUB:
        return new FormGroup({
          roleArn: new FormControl(credentials.roleArn),
          region: new FormControl(credentials.region),
        });
      case Integration.SNYK:
        return new FormGroup({
          apiToken: new FormControl(credentials.apiToken),
          orgId: new FormControl(credentials.orgId),
          url: new FormControl(credentials.url),
        });
      case Integration.INTUNE:
        return new FormGroup({
          tenantId: new FormControl(credentials.tenantId),
          clientId: new FormControl(credentials.clientId),
          clientSecret: new FormControl(credentials.clientSecret),
        });
      case Integration.SENTINEL:
        return new FormGroup({
          tenantId: new FormControl(credentials.tenantId),
          subscriptionId: new FormControl(credentials.subscriptionId),
          clientId: new FormControl(credentials.clientId),
          clientSecret: new FormControl(credentials.clientSecret),
          resourceGroupName: new FormControl(credentials.resourceGroupName),
          workspaceName: new FormControl(credentials.workspaceName),
        });
      case Integration.SENTINELONE:
        return new FormGroup({
          url: new FormControl(credentials.url),
          apiToken: new FormControl(credentials.apiToken),
        });
      case Integration.WIZ:
        return new FormGroup({
          apiEndpointUrl: new FormControl(credentials.apiEndpointUrl),
          clientId: new FormControl(credentials.clientId),
          clientSecret: new FormControl(credentials.clientSecret),
          resourceGroupName: new FormControl(credentials.resourceGroupName),
          workspaceName: new FormControl(credentials.workspaceName),
        });
      case Integration.SPLUNK:
        return new FormGroup({
          url: new FormControl(credentials.url),
          username: new FormControl(credentials.username),
          password: new FormControl(credentials.password),
          bearerToken: new FormControl(credentials.bearerToken),
        });
      case Integration.OKTA:
        return new FormGroup({
          url: new FormControl(credentials.url),
          apiToken: new FormControl(credentials.apiToken),
        });
      case Integration.ORCA:
        return new FormGroup({
          apiToken: new FormControl(credentials.apiToken),
          url: new FormControl(credentials.url),
        });
      case Integration.DUO:
        return new FormGroup({
          apiHostname: new FormControl(credentials.apiHostname),
          integrationKey: new FormControl(credentials.integrationKey),
          secretKey: new FormControl(credentials.secretKey),
        });
      case Integration.GITHUBSECURITY:
        return new FormGroup({
          token: new FormControl(credentials.token),
          organization: new FormControl(credentials.organization),
        });
      case Integration.GITLAB:
        return new FormGroup({
          token: new FormControl(credentials.token),
          url: new FormControl(credentials.url),
        });
      case Integration.VERACODE:
        return new FormGroup({
          url: new FormControl(credentials.url),
          apiId: new FormControl(credentials.apiId),
          apiKey: new FormControl(credentials.apiKey),
        });
      case Integration.SONARQUBE:
        return new FormGroup({
          url: new FormControl(credentials.url),
          token: new FormControl(credentials.token),
        });
      case Integration.KNOWBE4:
        return new FormGroup({
          apiToken: new FormControl(credentials.apiToken),
          region: new FormControl(credentials.region || 'us'),
        });
      case Integration.GCPSCC:
        return new FormGroup({
          serviceAccountKey: new FormControl(credentials.serviceAccountKey),
          organizationId: new FormControl(credentials.organizationId),
        });
      case Integration.SERVICENOWCMDB:
        return new FormGroup({
          url: new FormControl(credentials.url),
          username: new FormControl(credentials.username),
          password: new FormControl(credentials.password),
        });
      default:
        const controls: any = {
          url: new FormControl(credentials.url),
          username: new FormControl(credentials.username),
          password: new FormControl(credentials.password),
          accessKey: new FormControl(credentials.accessKey),
          secretKey: new FormControl(credentials.secretKey),
        };
        if (this.serviceName === Integration.QUALYSETM || this.serviceName === Integration.QUALYSVMDR) {
          controls.product = new FormControl(credentials.product || 'VMDR', [Validators.required]);
        }
        return new FormGroup(controls);
    }
  }

  async onSubmit(): Promise<void> {
    this.formDisabled = true;
    const formDataControl = this.formData.controls;

    let integrationArray: IntegrationModal[] = [];
    if (
      ((this.serviceName === ServiceName.AZUREDEFENDER || this.serviceName === ServiceName.MICROSOFTDEFENDER) &&
        this.isDefenderFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.AWSSECURITYHUB && this.isAwsFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.SNYK && formDataControl?.apiToken?.value && formDataControl?.orgId?.value) ||
      (this.serviceName === ServiceName.INTUNE && this.isIntuneFormValid(formDataControl)) ||
      (this.serviceName === Integration.SENTINEL && this.isSentinelFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.SENTINELONE &&
        formDataControl?.url?.value &&
        formDataControl?.apiToken?.value) ||
      (this.serviceName === ServiceName.WIZ && this.isWizFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.SPLUNK && this.isSplunkFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.OKTA && this.isOktaFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.ORCA && this.isOrcaFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.DUO && this.isDuoFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.GITHUBSECURITY && this.isGithubSecurityFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.GITLAB && this.isGitlabFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.VERACODE && this.isVeracodeFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.SONARQUBE && this.isSonarqubeFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.KNOWBE4 && this.isKnowbe4FormValid(formDataControl)) ||
      (this.serviceName === ServiceName.GCPSCC && this.isGcpsccFormValid(formDataControl)) ||
      (this.serviceName === ServiceName.SERVICENOWCMDB &&
        formDataControl?.url?.value &&
        formDataControl?.username?.value &&
        formDataControl?.password?.value) ||
      (this.serviceName === ServiceName.CYBERARK &&
        formDataControl?.url?.value &&
        formDataControl?.username?.value &&
        formDataControl?.password?.value) ||
      (formDataControl?.url?.value &&
        ((this.currView === CURR_VIEW.USERNAME &&
          formDataControl?.username?.value &&
          formDataControl?.password?.value) ||
          (this.currView === CURR_VIEW.ACCESSKEY &&
            formDataControl?.accessKey?.value &&
            formDataControl?.secretKey?.value)) &&
        // No additional validation needed for Qualys since product is automatic
        // Additional validation for Qualys product selection
        ((this.serviceName !== Integration.QUALYSETM && this.serviceName !== Integration.QUALYSVMDR) ||
          formDataControl?.product?.value))
    ) {
      let info: IntegrationCreds = {
        url: formDataControl?.url?.value,
        currView: this.currView,
      };

      if (this.serviceName === ServiceName.AZUREDEFENDER || this.serviceName === ServiceName.MICROSOFTDEFENDER) {
        info = {};
        info.subscriptionId = formDataControl.subscriptionId.value;
        info.tenantId = formDataControl.tenantId.value;
        info.clientId = formDataControl.clientId.value;
        info.clientSecret = formDataControl.clientSecret.value;
      } else if (this.serviceName === ServiceName.AWSSECURITYHUB) {
        info = {};
        info.roleArn = formDataControl.roleArn.value;
        info.region = formDataControl.region.value;
      } else if (this.serviceName === ServiceName.SNYK) {
        info = {};
        info.apiToken = formDataControl.apiToken.value;
        info.orgId = formDataControl.orgId.value;
        info.url = formDataControl.url.value;
      } else if (this.serviceName === ServiceName.INTUNE) {
        info = {};
        info.tenantId = formDataControl.tenantId.value;
        info.clientId = formDataControl.clientId.value;
        info.clientSecret = formDataControl.clientSecret.value;
      } else if (this.serviceName === Integration.SENTINEL) {
        info = {};
        info.tenantId = formDataControl.tenantId.value;
        info.subscriptionId = formDataControl.subscriptionId.value;
        info.clientId = formDataControl.clientId.value;
        info.clientSecret = formDataControl.clientSecret.value;
        info.resourceGroupName = formDataControl.resourceGroupName.value;
        info.workspaceName = formDataControl.workspaceName.value;
      } else if (this.serviceName === ServiceName.SENTINELONE) {
        info = {};
        info.url = formDataControl.url.value;
        info.apiToken = formDataControl.apiToken.value;
      } else if (this.serviceName === ServiceName.WIZ) {
        info = {};
        info.apiEndpointUrl = formDataControl.apiEndpointUrl.value;
        info.clientId = formDataControl.clientId.value;
        info.clientSecret = formDataControl.clientSecret.value;
      } else if (this.serviceName === ServiceName.SPLUNK) {
        info = { url: formDataControl.url.value, currView: this.currView };
        if (this.currView === CURR_VIEW.USERNAME) {
          info.username = formDataControl.username.value;
          info.password = formDataControl.password.value;
        } else {
          info.bearerToken = formDataControl.bearerToken.value;
        }
      } else if (this.serviceName === ServiceName.OKTA) {
        info = {};
        info.url = formDataControl.url.value;
        info.apiToken = formDataControl.apiToken.value;
      } else if (this.serviceName === ServiceName.ORCA) {
        info = {};
        info.apiToken = formDataControl.apiToken.value;
        info.url = formDataControl.url.value;
      } else if (this.serviceName === ServiceName.DUO) {
        info = {};
        info.apiHostname = formDataControl.apiHostname.value;
        info.integrationKey = formDataControl.integrationKey.value;
        info.secretKey = formDataControl.secretKey.value;
      } else if (this.serviceName === ServiceName.GITHUBSECURITY) {
        info = {};
        info.token = formDataControl.token.value;
        info.organization = formDataControl.organization.value;
      } else if (this.serviceName === ServiceName.GITLAB) {
        info = {};
        info.token = formDataControl.token.value;
        info.url = formDataControl.url.value;
      } else if (this.serviceName === ServiceName.VERACODE) {
        info = {};
        info.url = formDataControl.url.value;
        info.apiId = formDataControl.apiId.value;
        info.apiKey = formDataControl.apiKey.value;
      } else if (this.serviceName === ServiceName.SONARQUBE) {
        info = {};
        info.url = formDataControl.url.value;
        info.token = formDataControl.token.value;
      } else if (this.serviceName === ServiceName.KNOWBE4) {
        info = {};
        info.apiToken = formDataControl.apiToken.value;
        info.region = formDataControl.region.value;
      } else if (this.serviceName === ServiceName.GCPSCC) {
        info = {};
        info.serviceAccountKey = formDataControl.serviceAccountKey.value;
        info.organizationId = formDataControl.organizationId.value;
      } else if (this.serviceName === ServiceName.SERVICENOWCMDB) {
        info = {};
        info.url = formDataControl.url.value;
        info.username = formDataControl.username.value;
        info.password = formDataControl.password.value;
      } else if (this.serviceName === ServiceName.CYBERARK) {
        info = {};
        info.url = formDataControl.url.value;
        info.username = formDataControl.username.value;
        info.password = formDataControl.password.value;
      } else if (this.currView === CURR_VIEW.USERNAME) {
        info.username = formDataControl.username.value;
        info.password = formDataControl.password.value;
      } else {
        info.accessKey = formDataControl.accessKey.value;
        info.secretKey = formDataControl.secretKey.value;
      }

      // Add product selection for Qualys
      if (
        (this.serviceName === Integration.QUALYSETM || this.serviceName === Integration.QUALYSVMDR) &&
        formDataControl?.product?.value
      ) {
        info.product = formDataControl.product.value;
      }
      if (this.entity?.integrations) {
        integrationArray = this.entity.integrations.map(integration => {
          delete integration.__typename;
          return { ...integration };
        });
      }
      const integrationInfo = this.setIntegrationInfo(info);

      // Build scope payload for the new EntityIntegration table
      const rootEntityId = this.entity.rootEntityId || this.entity.parentId || this.entityId;
      // At sub-entity level the picker is hidden, so ignore stale sub-entity selections
      const selectedSubEntityIds = this.isSubEntityLevel
        ? []
        : this.subEntities.filter(se => se.selected).map(se => se.id);

      // Frontend owns filePath computation based on scoping intent.
      // If the integration is already authenticated, preserve the existing filePath
      // so a re-run doesn't accidentally move the S3 evidence file.
      const service = this.serviceType.toLowerCase();
      const isApplyHere = !this.applyToAll && !selectedSubEntityIds.length && this.isSubEntityLevel;
      let filePath: string | undefined;
      if (this.existingIsAuth !== true) {
        filePath = isApplyHere
          ? `public/CLIENTS/${rootEntityId}/INTEGRATIONS/${this.subEntityId}/${service}.json`
          : `public/CLIENTS/${rootEntityId}/INTEGRATIONS/${service}.json`;
      }

      const payload: any = {
        id: isApplyHere ? this.subEntityId : this.entityId,
        rootEntityId,
        currView: this.currView,
        newlyAddedIntegration: integrationInfo,
        type: 'Mutation',
        source: 'frontend',
        flow: 'COLLECTION_INTEGRATION_FLOW',
      };

      // Only send filePath when it needs to change (new integration or previously failed auth)
      if (filePath) {
        payload.filePath = filePath;
      }

      if (this.applyToAll) {
        payload.applyToAll = true;
      } else if (selectedSubEntityIds.length) {
        payload.subEntityIds = selectedSubEntityIds;
      }
      // If neither applyToAll nor subEntityIds, backend treats it as "Apply here" for payload.id

      try {
        const integrationResponse = await this.customApi.HandleIntegrationDetails(JSON.stringify(payload));
        const integrationReadable = await integrationResponse.body.json();
        if (integrationReadable && integrationReadable.data) {
          // Refresh entity to pick up the latest state
          this.entity = await this.getEntity();
          this.collectionService.updateIntegrationTabs$.next(this.entity);
          this.toastrService.success('Information has been saved.');

          // Extract EntityIntegration record IDs from response for later status updates
          const responseData =
            typeof integrationReadable.data === 'string'
              ? JSON.parse(integrationReadable.data)
              : integrationReadable.data;
          const entityIntegrationIds = responseData?.entityIntegrationIds || [];

          this.runGetSites(entityIntegrationIds, selectedSubEntityIds);
        }
      } catch (e) {
        console.error('Failed to save integration details:', e);
        this.toastrService.error('Failed to save integration. Please try again.');
      }
    } else {
      this.toastrService.error('Missing required fields');
    }
    this.formDisabled = false;
  }

  runGetSites(entityIntegrationIds: string[] = [], subEntityIds: string[] = []): void {
    const eventPayload: any = {
      entity: this.entity,
      currService: this.serviceName,
      entityIntegrationIds,
      subEntityIds,
      appliedToAll: this.applyToAll,
    };
    this.accountEntity.emit(eventPayload);
  }

  /**
   * Opens a confirmation modal before removing the integration.
   * Message varies based on scope: all entities, specific count, or current sub-entity.
   */
  confirmRemoveIntegration(): void {
    if (this.removeInProgress) {
      return;
    }

    const integrationDisplayName = this.title || this.serviceName || 'this integration';
    let removeFromText: string;
    if (this.applyToAll && !this.isSubEntityLevel) {
      removeFromText = ' from all entities';
    } else if (!this.isSubEntityLevel) {
      const count = this.selectedSubEntityCount;
      if (count > 1) {
        removeFromText = ` from ${count} entities`;
      } else if (count === 1) {
        const selectedName = this.subEntities.find(se => se.selected)?.name || 'the selected entity';
        removeFromText = ` from ${selectedName}`;
      } else {
        removeFromText = ` from ${this.entity?.name || 'this entity'}`;
      }
    } else {
      const subEntityName = this.subEntities.find(se => se.id === this.subEntityId)?.name;
      const entityName = subEntityName || this.entity?.name || 'this entity';
      removeFromText = ` from ${entityName}`;
    }

    const modalRef = this.modalService.open(DeleteModalComponent, {
      size: 'sm',
      windowClass: 'remove-integration-modal',
      backdropClass: 'remove-integration-backdrop',
      backdrop: 'static',
    });
    modalRef.componentInstance.titleText = 'REMOVE INTEGRATION';
    modalRef.componentInstance.removeLabelText = integrationDisplayName;
    modalRef.componentInstance.removeFromText = removeFromText;
    modalRef.componentInstance.removeOrCloseText = 'remove';
    modalRef.componentInstance.svgName = 'trash-icon';
    modalRef.componentInstance.isFromScans = true;
    modalRef.componentInstance.showPermanentText = false;
    modalRef.componentInstance.deleteFramework.subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.removeIntegration();
      }
    });
  }

  /**
   * Removes the integration for the selected scope (Apply to All / specific sub-entities / here).
   * Deletes the EntityIntegration record(s) and the simplified legacy Entity.integrations entry.
   */
  async removeIntegration(): Promise<void> {
    if (this.removeInProgress) {
      return;
    }
    this.removeInProgress = true;
    this.toastrService.info('Initiating integration removal...');
    try {
      const rootEntityId = this.entity.rootEntityId || this.entity.parentId || this.entityId;
      // At sub-entity level the picker is hidden, so ignore stale sub-entity selections
      const selectedSubEntityIds = this.isSubEntityLevel
        ? []
        : this.subEntities.filter(se => se.selected).map(se => se.id);

      const payload: any = {
        id: this.isSubEntityLevel ? this.subEntityId : this.entityId,
        rootEntityId,
        service: this.serviceType,
        type: 'Delete',
        source: 'frontend',
        flow: 'COLLECTION_INTEGRATION_FLOW',
      };
      if (this.isSubEntityLevel) {
        // Sub-entity level delete: send as a scoped removal so the backend
        // fans out the appliedToAll root record to other children and only
        // disconnects this sub-entity.
        payload.subEntityIds = [this.subEntityId];
      } else if (this.applyToAll) {
        payload.applyToAll = true;
      } else if (selectedSubEntityIds.length) {
        payload.subEntityIds = selectedSubEntityIds;
      }

      await this.customApi.HandleIntegrationDetails(JSON.stringify(payload));

      // Invalidate the EntityIntegration cache so the UI picks up fresh data
      this.integrationMappingService.invalidateCache(rootEntityId);

      this.entity = await this.getEntity();
      this.collectionService.updateIntegrationTabs$.next(this.entity);
      this.toastrService.success('Integration has been removed.');
      this.closeModal();
    } catch (e) {
      console.error('Failed to remove integration:', e);
      this.toastrService.error('Failed to remove integration.');
    } finally {
      this.removeInProgress = false;
    }
  }

  /**
   * Fetches decrypted credentials from the backend.
   * Returns parsed credential object or empty defaults.
   */
  private async _fetchCredentials(
    rootEntityId: string,
    querySubEntityId: string | null,
    queryAppliedToAll: boolean
  ): Promise<IntegrationCreds> {
    let credentials: IntegrationCreds = { url: null, username: null, password: null, product: null };
    const payload = {
      id: querySubEntityId || this.entityId,
      rootEntityId,
      subEntityId: querySubEntityId,
      appliedToAll: queryAppliedToAll,
      service: this.serviceType,
      type: 'Query',
      source: 'frontend',
      flow: 'COLLECTION_INTEGRATION_FLOW',
    };

    try {
      const integration = await this.customApi.HandleIntegrationDetails(JSON.stringify(payload));
      const integrationReadable = await integration.body.json();
      if (integrationReadable && integrationReadable.data) {
        const integrationData = integrationReadable.data;
        credentials = JSON.parse(integrationData.credentials);
        if (credentials?.currView) {
          this.currView = credentials.currView;
          this.savedViewState = credentials.currView;
        }
        this.existingIsAuth = integrationData.isAuth;
      }
    } catch (e) {
      console.log('Issue fetching credentials', e);
    }
    return credentials;
  }

  /**
   * Called when user clicks the eye icon to reveal masked credentials.
   * Makes the HandleIntegrationDetails Query call and populates form fields.
   */
  async revealCredentials(): Promise<void> {
    if (this.credentialsRevealed || this.credentialsLoading) {
      return;
    }
    this.credentialsLoading = true;
    try {
      const rootEntityId = this.entity.rootEntityId || this.entity.parentId || this.entityId;
      const credentials = await this._fetchCredentials(rootEntityId, this._querySubEntityId, this._queryAppliedToAll);
      // Patch form fields with the real values
      const patch: any = {};
      Object.keys(this.formData.controls).forEach(key => {
        if (credentials[key] !== undefined && credentials[key] !== null) {
          patch[key] = credentials[key];
        }
      });
      this.formData.enable();
      this.formData.patchValue(patch);
      this.credentialsRevealed = true;
    } catch (e) {
      this.toastrService.error('Failed to load credentials.');
      console.error('revealCredentials error:', e);
    } finally {
      this.credentialsLoading = false;
    }
  }

  setIntegrationInfo(info): IntegrationModal {
    return {
      name: this.serviceType,
      credentials: JSON.stringify(info),
      isEnable: true,
      isAuth: null,
      inheritFromParent: null,
    };
  }

  async loadSubEntities(): Promise<void> {
    try {
      this.subEntitiesLoading = true;
      const children = await this.rootEntityContextService.getSubEntities(EntityTypeEnum.CHILD_ENTITY, this.entityId);
      this.subEntities = (children || []).map(child => ({
        id: child.id,
        name: child.name,
        selected: this.applyToAll,
      }));
    } catch (e) {
      console.error('Failed to load sub-entities for integration scoping', e);
    } finally {
      this.subEntitiesLoading = false;
    }
  }

  setApplyScope(all: boolean): void {
    this.applyToAll = all;
    if (all) {
      this.subEntities.forEach(subEntity => (subEntity.selected = true));
      this.showSubEntityPicker = false;
    } else {
      this.subEntities.forEach(subEntity => (subEntity.selected = false));
    }
  }

  toggleSubEntityPicker(): void {
    // Opening the picker implies scoping to specific sub-entities
    if (this.applyToAll) {
      this.setApplyScope(false);
      this.showSubEntityPicker = true;
      return;
    }
    this.showSubEntityPicker = !this.showSubEntityPicker;
  }

  toggleSubEntity(subEntity: { id: string; name: string; selected: boolean }, checked: boolean): void {
    subEntity.selected = checked;
    if (!checked) {
      this.applyToAll = false;
    }
  }

  get selectedSubEntityCount(): number {
    return this.subEntities.filter(se => se.selected).length;
  }

  async getEntity(): Promise<GetEntityQuery> {
    return await this.rootEntityContextService.getRootEntityById(this.entityId);
  }

  closeModal(): void {
    this.activeModal.close();
  }

  setPlaceholder(serviceName: string): void {
    switch (serviceName && serviceName.toLowerCase()) {
      case Integration.TENABLE:
        this.placeholder = this.tenablePlaceholder;
        break;
      case Integration.NESSUS:
        this.placeholder = this.nessusPlaceholder;
        break;
      case Integration.CROWDSTRIKE:
        this.placeholder = this.crowdstrikePlaceholder;
        break;
      case Integration.RAPID7:
        this.placeholder = this.r7Placeholder;
        break;
      case Integration.QUALYSETM:
        this.placeholder = this.qualysPlaceholder;
        break;
      case Integration.QUALYSVMDR:
        this.placeholder = this.qualysVMDRPlaceholder;
        break;
      case Integration.PRISMACLOUD:
        this.placeholder = this.prismacloudPlaceholder;
        break;
      default:
        break;
    }
  }

  toggleTenableSwitch(view: View): void {
    this.currView = view;
    if (this.savedViewState && view !== this.savedViewState) {
      this.hasCredentials = false;
      this.credentialsRevealed = false;
      this.formData.reset();
      this.formData.enable();
    } else if (this.savedViewState && view === this.savedViewState) {
      this.hasCredentials = true;
      this.credentialsRevealed = false;
      const mask = '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022';
      Object.keys(this.formData.controls).forEach(key => this.formData.controls[key].setValue(mask));
      this.formData.disable();
    }
  }

  updateExistingCredentials(existingIntegrationInfo: IntegrationCreds, formDataControl: any): void {
    if (this.serviceName === ServiceName.AZUREDEFENDER || this.serviceName === ServiceName.MICROSOFTDEFENDER) {
      existingIntegrationInfo.subscriptionId = formDataControl.subscriptionId.value;
      existingIntegrationInfo.tenantId = formDataControl.tenantId.value;
      existingIntegrationInfo.clientId = formDataControl.clientId.value;
      existingIntegrationInfo.clientSecret = formDataControl.clientSecret.value;
      return;
    }

    if (this.serviceName === ServiceName.AWSSECURITYHUB) {
      existingIntegrationInfo.roleArn = formDataControl.roleArn.value;
      existingIntegrationInfo.region = formDataControl.region.value;
      return;
    }

    if (this.serviceName === ServiceName.SNYK) {
      existingIntegrationInfo.apiToken = formDataControl.apiToken.value;
      existingIntegrationInfo.orgId = formDataControl.orgId.value;
      existingIntegrationInfo.url = formDataControl.url.value;
      return;
    }

    if (this.serviceName === ServiceName.INTUNE) {
      existingIntegrationInfo.tenantId = formDataControl.tenantId.value;
      existingIntegrationInfo.clientId = formDataControl.clientId.value;
      existingIntegrationInfo.clientSecret = formDataControl.clientSecret.value;
      return;
    }

    if (this.serviceName === Integration.SENTINEL) {
      existingIntegrationInfo.tenantId = formDataControl.tenantId.value;
      existingIntegrationInfo.subscriptionId = formDataControl.subscriptionId.value;
      existingIntegrationInfo.clientId = formDataControl.clientId.value;
      existingIntegrationInfo.clientSecret = formDataControl.clientSecret.value;
      existingIntegrationInfo.resourceGroupName = formDataControl.resourceGroupName.value;
      existingIntegrationInfo.workspaceName = formDataControl.workspaceName.value;
      return;
    }

    if (this.serviceName === ServiceName.SENTINELONE) {
      existingIntegrationInfo.url = formDataControl.url.value;
      existingIntegrationInfo.apiToken = formDataControl.apiToken.value;
      return;
    }

    if (this.serviceName === ServiceName.WIZ) {
      existingIntegrationInfo.apiEndpointUrl = formDataControl.apiEndpointUrl.value;
      existingIntegrationInfo.clientId = formDataControl.clientId.value;
      existingIntegrationInfo.clientSecret = formDataControl.clientSecret.value;
      return;
    }

    if (this.serviceName === ServiceName.SPLUNK) {
      existingIntegrationInfo.url = formDataControl.url.value;
      existingIntegrationInfo.currView = this.currView;
      if (this.currView === CURR_VIEW.USERNAME) {
        existingIntegrationInfo.username = formDataControl.username.value;
        existingIntegrationInfo.password = formDataControl.password.value;
      } else {
        existingIntegrationInfo.bearerToken = formDataControl.bearerToken.value;
      }
      return;
    }

    if (this.serviceName === ServiceName.OKTA) {
      existingIntegrationInfo.url = formDataControl.url.value;
      existingIntegrationInfo.apiToken = formDataControl.apiToken.value;
      return;
    }

    if (this.serviceName === ServiceName.ORCA) {
      existingIntegrationInfo.apiToken = formDataControl.apiToken.value;
      existingIntegrationInfo.url = formDataControl.url.value;
      return;
    }

    if (this.serviceName === ServiceName.GITHUBSECURITY) {
      existingIntegrationInfo.token = formDataControl.token.value;
      existingIntegrationInfo.organization = formDataControl.organization.value;
      return;
    }

    if (this.serviceName === ServiceName.GITLAB) {
      existingIntegrationInfo.token = formDataControl.token.value;
      existingIntegrationInfo.url = formDataControl.url.value;
      return;
    }

    if (this.serviceName === ServiceName.VERACODE) {
      existingIntegrationInfo.url = formDataControl.url.value;
      existingIntegrationInfo.apiId = formDataControl.apiId.value;
      existingIntegrationInfo.apiKey = formDataControl.apiKey.value;
      return;
    }

    if (this.serviceName === ServiceName.SONARQUBE) {
      existingIntegrationInfo.url = formDataControl.url.value;
      existingIntegrationInfo.token = formDataControl.token.value;
      return;
    }

    if (this.serviceName === ServiceName.KNOWBE4) {
      existingIntegrationInfo.apiToken = formDataControl.apiToken.value;
      existingIntegrationInfo.region = formDataControl.region.value;
      return;
    }

    if (this.serviceName === ServiceName.GCPSCC) {
      existingIntegrationInfo.serviceAccountKey = formDataControl.serviceAccountKey.value;
      existingIntegrationInfo.organizationId = formDataControl.organizationId.value;
      return;
    }

    if (this.serviceName === ServiceName.SERVICENOWCMDB) {
      existingIntegrationInfo.url = formDataControl.url.value;
      existingIntegrationInfo.username = formDataControl.username.value;
      existingIntegrationInfo.password = formDataControl.password.value;
      return;
    }

    if (!UtilsService.isDefined(existingIntegrationInfo.currView) || this.currView === CURR_VIEW.USERNAME) {
      existingIntegrationInfo.username = formDataControl.username.value;
      existingIntegrationInfo.password = formDataControl.password.value;
    } else {
      existingIntegrationInfo.accessKey = formDataControl.accessKey.value;
      existingIntegrationInfo.secretKey = formDataControl.secretKey.value;
    }
    existingIntegrationInfo.url = formDataControl.url.value;
    existingIntegrationInfo.currView = this.currView;

    // Add product selection for Qualys
    if (
      (this.serviceName === Integration.QUALYSETM || this.serviceName === Integration.QUALYSVMDR) &&
      formDataControl?.product?.value
    ) {
      existingIntegrationInfo.product = formDataControl.product.value;
    }
  }

  async updateCredentials(
    existingCredentials: IntegrationCreds,
    existingItemIndex: number,
    integrationArray: IntegrationModal[]
  ): Promise<void> {
    if (existingCredentials?.currView !== this.currView) {
      existingCredentials.currView = this.currView;
      integrationArray[existingItemIndex] = this.setIntegrationInfo(existingCredentials);
      await this.entityService.updateEntity({
        id: this.entity.id,
        integrations: integrationArray,
      });
    }
  }

  isDefenderFormValid(credentials): boolean {
    if (
      credentials?.subscriptionId?.value &&
      credentials?.tenantId?.value &&
      credentials?.clientId?.value &&
      credentials?.clientSecret?.value
    ) {
      return true;
    }

    return false;
  }

  isAwsFormValid(credentials): boolean {
    if (credentials?.roleArn?.value && credentials?.region?.value) {
      return true;
    }

    return false;
  }

  isIntuneFormValid(credentials): boolean {
    if (credentials?.tenantId?.value && credentials?.clientId?.value && credentials?.clientSecret?.value) {
      return true;
    }

    return false;
  }

  isSentinelFormValid(credentials): boolean {
    if (
      credentials?.tenantId?.value &&
      credentials?.subscriptionId?.value &&
      credentials?.clientId?.value &&
      credentials?.clientSecret?.value &&
      credentials?.resourceGroupName?.value &&
      credentials?.workspaceName?.value
    ) {
      return true;
    }

    return false;
  }

  isWizFormValid(credentials): boolean {
    if (credentials?.apiEndpointUrl?.value && credentials?.clientId?.value && credentials?.clientSecret?.value) {
      return true;
    }

    return false;
  }

  isSplunkFormValid(credentials): boolean {
    if (!credentials?.url?.value) {
      return false;
    }
    if (this.currView === CURR_VIEW.USERNAME) {
      return !!(credentials?.username?.value && credentials?.password?.value);
    }
    return !!credentials?.bearerToken?.value;
  }

  isOktaFormValid(credentials): boolean {
    if (credentials?.url?.value && credentials?.apiToken?.value) {
      return true;
    }

    return false;
  }

  isOrcaFormValid(credentials): boolean {
    if (credentials?.apiToken?.value && credentials?.url?.value) {
      return true;
    }

    return false;
  }

  isGithubSecurityFormValid(credentials): boolean {
    if (credentials?.token?.value && credentials?.organization?.value) {
      return true;
    }

    return false;
  }

  isGitlabFormValid(credentials): boolean {
    if (credentials?.token?.value && credentials?.url?.value) {
      return true;
    }

    return false;
  }

  isVeracodeFormValid(credentials): boolean {
    if (credentials?.url?.value && credentials?.apiId?.value && credentials?.apiKey?.value) {
      return true;
    }

    return false;
  }

  isDuoFormValid(credentials): boolean {
    if (credentials?.apiHostname?.value && credentials?.integrationKey?.value && credentials?.secretKey?.value) {
      return true;
    }

    return false;
  }

  isSonarqubeFormValid(credentials): boolean {
    if (credentials?.url?.value && credentials?.token?.value) {
      return true;
    }

    return false;
  }

  isKnowbe4FormValid(credentials): boolean {
    if (credentials?.apiToken?.value && credentials?.region?.value) {
      return true;
    }

    return false;
  }

  isGcpsccFormValid(credentials): boolean {
    if (credentials?.serviceAccountKey?.value && credentials?.organizationId?.value) {
      return true;
    }

    return false;
  }

  private toTitleCase(value: string): string {
    if (!value) {
      return '';
    }
    return value
      .replace(/[^a-zA-Z0-9]+(.)/g, (_, char) => char.toUpperCase())
      .replace(/^(.)/, char => char.toUpperCase());
  }

  async downloadGuide() {
    let recurringInterval: any = null;
    try {
      this.toastrService.info(`Downloading ${this.toTitleCase(this.serviceName)} guide it might take a few moments...`);

      recurringInterval = setInterval(() => {
        this.toastrService.info(`Downloading ${this.toTitleCase(this.serviceName)} guide, please wait...`);
      }, 20000);

      const url = await this.fileService.getPresignedS3DownloadUrl(
        `public/RELATIONS/INTEGRATIONS/INTEGRATION_GUIDES/${this.serviceName.toUpperCase()}_GUIDE.pdf`
      );

      const file = await fetch(url);

      if (file.status === 200) {
        const blob = await file.blob();
        clearInterval(recurringInterval);
        recurringInterval = null;
        this.toastrService.clear();
        saveAs(blob, `${this.toTitleCase(this.serviceName)}Guide.pdf`);
        this.toastrService.success(`${this.toTitleCase(this.serviceName)} Guide Downloaded Successfully`);
      }
    } catch {
      this.toastrService.clear();
      this.toastrService.error(`Error downloading ${this.toTitleCase(this.serviceName)} guide`);
    } finally {
      if (recurringInterval) {
        clearInterval(recurringInterval);
      }
    }
  }
}
